import { FC } from 'react';
import { Col, Row } from 'antd';
import { beautifyTheLowHighObject, replaceEmpty } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import V2Title from '@/common/components/Feedback/V2Title';
import { ModuleDetailsType, BidManagementModuleType } from '../type';
import styles from './BidManagementModule.module.less';
import IconFont from '@/common/components/Base/IconFont';

interface BidManagementModuleProps {
  data: ModuleDetailsType;
}

const COLUMNS = [
  {
    key: 'current',
    title: '当前情况',
    cardBg: '#FFFAEF',
    tagBg: '#FFC136',
    borderColor: '#FFEBBD',
    tooltip: '当前已录入或预估的实际租金水平',
  },
  {
    key: 'baseline',
    title: '基线情况',
    cardBg: '#F3F8FF',
    tagBg: '#66A8FF',
    borderColor: '#DAEAFF',
    tooltip: '基于目标基线盈利周期，推算出的租金谈判上限',
  },
  {
    key: 'ideal',
    title: '理想情况',
    cardBg: '#EBFCF6',
    tagBg: '#00D188',
    borderColor: '#CBF2E4',
    tooltip: '基于目标理想盈利周期，推算出的租金谈判上限',
  },
];

const BidManagementModule: FC<BidManagementModuleProps> = ({ data }) => {
  const mod: BidManagementModuleType | undefined = data.bidManagementModule;

  const methods = useMethods({
    formatRent(val: string): string {
      if (!val) return replaceEmpty(val);
      const res = beautifyTheLowHighObject(1, Number(val), 2, { low: '元', high: '万元', top: '亿元' });
      return `${res.val}${res.unit}/月`;
    },
    formatSales(val: string): string {
      if (!val) return replaceEmpty(val);
      const res = beautifyTheLowHighObject(1, Number(val), 2, { low: '元', high: '万元', top: '亿元' });
      return `${res.val}${res.unit}/天`;
    },
    formatCostPerCustomer(val: string): string {
      if (!val) return replaceEmpty(val);
      const res = beautifyTheLowHighObject(1, Number(val), 2, { low: '元', high: '万元', top: '亿元' });
      return `${res.val}${res.unit}/人`;
    },
    isCurrentRentOverBase(): boolean {
      if (!mod?.currentRent || !mod?.baselineRent) return false;
      return Number(mod.currentRent) > Number(mod.baselineRent) * 1.3;
    },
    getColData(key: string): any {
      if (!mod) return { rent: null, sales: null, costPerCustomer: null };
      if (key === 'current') return { rent: mod.currentRent, sales: mod.currentEstimatedDailySales, costPerCustomer: mod.currentRentCostPerCustomer };
      if (key === 'baseline') return { rent: mod.baselineRent, sales: mod.baselineEstimatedDailySales, costPerCustomer: mod.baselineRentCostPerCustomer };
      return { rent: mod.idealRent, sales: mod.idealEstimatedDailySales, costPerCustomer: mod.idealRentCostPerCustomer };
    },
  });

  const isCurrentOverBase = methods.isCurrentRentOverBase();

  return (
    <div>
      <V2Title type='H2' text={data.moduleTypeName} divider className='pt-16 pb-10' />
      <Row gutter={13}>
        {COLUMNS.map((col) => {
          const colData = methods.getColData(col.key);
          const rentOverBase = col.key === 'current' && isCurrentOverBase;
          return (
            <Col span={8} key={col.key}>
              <div className={styles.bidColumn} style={{ backgroundColor: col.cardBg, borderColor: col.borderColor }}>
                <span
                  className={styles.bidTag}
                  style={{ color: '#fff', backgroundColor: col.tagBg }}
                >
                  {col.title}
                </span>
                <div className={styles.bidRow}>
                  <span className={styles.bidLabel}>
                    租金
                    <IconFont className={styles.bidTip} iconHref='asset-pc-ic_question' tooltipConfig={{
                      title: col.tooltip,
                    }}/>
                  </span>
                  <span
                    className={styles.bidValue}
                    style={rentOverBase ? { color: '#ff4d4f' } : {}}
                  >
                    {methods.formatRent(colData.rent)}
                  </span>
                </div>
                <div className={styles.bidRow}>
                  <span className={styles.bidLabel}>预估日销</span>
                  <span className={styles.bidValue}>{methods.formatSales(colData.sales)}</span>
                </div>
                <div className={styles.bidRow}>
                  <span className={styles.bidLabel}>单过店客流租金成本</span>
                  <span className={styles.bidValue}>{methods.formatCostPerCustomer(colData.costPerCustomer)}</span>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default BidManagementModule;
